import { z } from 'zod';
import { RELATED_ENTITY_TYPES } from '../enums.js';

/**
 * Global search.
 *
 * One query across every module the caller can see, ranked so the best match
 * is first regardless of which module it came from. Deliberately shallow: it
 * returns enough to recognise and navigate to a record, not enough to work
 * from. Anyone who needs filters and columns wants the module's list view, and
 * a search box that tries to be both is worse at each.
 */
/**
 * `CUSTOM` covers every module an administrator defined, as one bucket.
 *
 * Not one entry per module: the list is per tenant and known only at runtime,
 * and a closed union is what lets the client render results without knowing
 * what a "project" is. Each hit carries its own href instead.
 */
export const SEARCHABLE_TYPES = [...RELATED_ENTITY_TYPES, 'ACTIVITY', 'CUSTOM'] as const;
export type SearchableType = (typeof SEARCHABLE_TYPES)[number];

export const searchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Type at least two characters').max(100),
  /** Narrow to one module; omitted, everything the caller can read. */
  type: z.enum(SEARCHABLE_TYPES).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export interface SearchHit {
  type: SearchableType;
  id: string;
  /** What the record is called, e.g. a person's name or a deal's title. */
  title: string;
  /** One line of context: the business, the account, the amount. */
  subtitle: string | null;
  /** Where clicking it goes. */
  href: string;
  /**
   * 0–1, higher is better. Computed the same way for every module so results
   * can be interleaved rather than grouped by module and ordered arbitrarily.
   */
  score: number;
}

export interface SearchResults {
  query: string;
  hits: SearchHit[];
  /** True when the cap was reached, so the UI can say "refine this". */
  truncated: boolean;
}

/** The route each module's records live at. */
export const SEARCH_ROUTES: Record<SearchableType, string> = {
  LEAD: '/leads',
  ACCOUNT: '/accounts',
  CONTACT: '/contacts',
  DEAL: '/deals',
  ACTIVITY: '/activities',
  // Only a prefix: a custom hit's full path needs the module's api name, so
  // the service sets `href` on the hit directly.
  CUSTOM: '/m',
};

/**
 * Where a hit navigates to.
 *
 * Activities have no page of their own — they are edited in a dialog over the
 * list — so a hit opens the list with that activity in view rather than
 * linking to a route that does not exist.
 */
export function searchHref(type: SearchableType, id: string): string {
  return type === 'ACTIVITY'
    ? `${SEARCH_ROUTES.ACTIVITY}?open=${id}`
    : `${SEARCH_ROUTES[type]}/${id}`;
}

export const SEARCH_TYPE_LABELS: Record<SearchableType, string> = {
  CUSTOM: 'Record',
  LEAD: 'Lead',
  ACCOUNT: 'Account',
  CONTACT: 'Contact',
  DEAL: 'Deal',
  ACTIVITY: 'Activity',
};

/**
 * Ranks a hit by how well the term matches its title.
 *
 * An exact match beats a prefix, which beats a match anywhere, which beats a
 * match only in the subtitle. Scoring in application code rather than in SQL
 * is what lets five separate queries be merged into one ordered list — and
 * ordering by `similarity()` per query would order each module against itself,
 * which is not the same thing.
 */
export function scoreMatch(term: string, title: string, subtitle?: string | null): number {
  const needle = term.trim().toLowerCase();
  const haystack = title.toLowerCase();

  if (haystack === needle) return 1;
  if (haystack.startsWith(needle)) return 0.9;

  // A match at a word boundary reads as more relevant than one mid-word:
  // "ann" should rank "Ann Patel" and "Susan Ann" above "Brannigan".
  const wordBoundary = haystack.split(/\s+/u).some((word) => word.startsWith(needle));
  if (wordBoundary) return 0.75;
  if (haystack.includes(needle)) return 0.6;
  if (subtitle?.toLowerCase().includes(needle)) return 0.4;

  return 0.2;
}
